/**
 * L2: Items tab (01-references §6 S3). It copies the 1Password iOS list: a category filter at
 * the top ("All Categories"), a sort control, A-Z sections, and Archive and Recently Deleted
 * at the bottom. Vault additions: replica age, and a named row for any wallet that failed to load.
 */
import { CATEGORY_LABEL, type AppActions, type AppState, type SortKey } from "../contract.js";
import { accountButton, banner, cell, emptyState, fakeChip, group, navBar, sheetFrame } from "../shell/chrome.js";
import { h, icon } from "../shell/dom.js";
import { isFavorite, itemsModel, type ListId } from "../vm/items.js";
import { replicaAge } from "../vm/staleness.js";
import { itemRow } from "./row.js";

export function renderItems(state: AppState, actions: AppActions): HTMLElement {
  const snap = state.snapshot!;
  const list: ListId = state.route.list ?? "all";
  const m = itemsModel(snap, { scope: state.scope, list, sort: state.sort, favoriteOverrides: state.favoriteOverrides });
  const age = replicaAge(snap.asOf, state.now);
  const isRoot = list === "all";

  const header = navBar({
    left: isRoot ? accountButton(state, actions) : h("button", { class: "navbar-btn", onclick: () => actions.navigate({ name: "items", list: "all" }) }, icon("back"), "All Items"),
    right: h("button", { class: "navbar-btn", "aria-label": "Sort", onclick: () => actions.openSheet({ kind: "sort" }) }, icon("sort")),
    largeTitle: m.title,
    largeTitleAccessory: snap.fixture ? fakeChip() : null,
  });

  const toolbar = h(
    "div",
    { class: "list-toolbar" },
    list === "archive" || list === "deleted"
      ? h("span", { class: "count" }, list === "deleted" ? "Deleted items stay here until the source vault purges them." : "Archived items are hidden from search.")
      : h(
          "button",
          { class: "filter-btn", onclick: () => actions.openSheet({ kind: "categories" }) },
          list === "all" || list === "favorites" ? "All Categories" : CATEGORY_LABEL[list],
          icon("chevron", "icon small"),
        ),
    h("span", { class: "count" }, `${m.count} item${m.count === 1 ? "" : "s"}`),
  );

  const notices = [
    age.level === "stale" ? banner("warn", "This copy may be out of date", `${age.label}. Open Vowed on your Mac to refresh it.`) : null,
    ...m.failed.map((f) => banner("error", `Couldn't load “${snap.wallets.find((w) => w.id === f.wallet)?.name ?? f.wallet}”`, f.reason)),
  ];

  const body = m.empty
    ? emptyState("items", list === "all" ? "No items" : `No ${m.title.toLowerCase()}`, list === "all" ? "Nothing in this copy of the vault yet." : "Nothing matches this list.")
    : h(
        "div",
        { class: "list" },
        ...m.sections.flatMap((s) => [
          s.header ? h("div", { class: "section-header" }, s.header) : null,
          ...s.items.map((i) => itemRow(i, snap.wallets, actions, { showWallet: state.scope === null, favorite: isFavorite(i, state.favoriteOverrides) })),
        ]),
      );

  const footer =
    isRoot && !m.empty
      ? h(
          "div",
          { class: "footer-lists" },
          h("div", { class: "section-header" }, " "),
          group(null, [
            cell({ label: "Archive", lead: icon("archive"), detail: String(m.archiveCount), chevron: true, onClick: () => actions.navigate({ name: "items", list: "archive" }) }),
            cell({ label: "Recently Deleted", lead: icon("trash"), detail: String(m.deletedCount), chevron: true, onClick: () => actions.navigate({ name: "items", list: "deleted" }) }),
          ]),
        )
      : null;

  return h("main", { class: "screen plain", "data-screen": "items" }, header, toolbar, ...notices, body, footer);
}

export function renderSortSheet(state: AppState, actions: AppActions): HTMLElement {
  const options: [SortKey, string][] = [
    ["title", "Title"],
    ["modified", "Date Modified"],
    ["frequency", "Frequency of Use"],
  ];
  return sheetFrame(
    "Sort By",
    actions,
    group(
      null,
      options.map(([k, label]) => cell({ label, check: state.sort === k, onClick: () => { actions.setSort(k); actions.closeSheet(); } })),
    ),
  );
}

export function renderCategoriesSheet(state: AppState, actions: AppActions): HTMLElement {
  const snap = state.snapshot!;
  const m = itemsModel(snap, { scope: state.scope, list: "all", sort: state.sort, favoriteOverrides: state.favoriteOverrides });
  const current = state.route.list ?? "all";
  const go = (list: ListId) => () => {
    actions.closeSheet();
    actions.navigate({ name: "items", list });
  };
  return sheetFrame(
    "Categories",
    actions,
    group(null, [
      cell({ label: "All Categories", detail: String(m.allCount), check: current === "all", onClick: go("all") }),
      cell({ label: "Favorites", lead: icon("star"), check: current === "favorites", onClick: go("favorites") }),
    ]),
    group(
      null,
      m.categoryCounts.map((c) => cell({ label: c.label, detail: String(c.count), check: current === c.category, onClick: go(c.category), disabled: c.count === 0 })),
    ),
  );
}
